import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Textarea from 'react-textarea-autosize';
import {
  Row,
  Col,
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { APIgetGroups } from '../../../api/Group';
import {
  APIcreateUser,
  APIupdateUser,
} from '../../../api/User';

import {
  parseResponseErrors,
  sendTrackingEvent,
} from '../../../helpers';
import { userHasFeature } from '../../../helpers/Features';
import { performOnboardingStep } from '../../../helpers/Onboarding';

import AdminUserEditRoleSelect from '../UserEdit/RoleSelect';
import AdminUsersLimitWarning from './LimitWarning';
import AccessModalList from '../../Access/ModalList';
import AccessModalSelect from '../../Access/ModalSelect';
import UpgradePlanButton from '../../UpgradePlanButton';
import ProcessingSpinner from '../../ProcessingSpinner';

import ErrorBoundary from '../../ErrorBoundary';

class UserEditModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      firstName: '',
      lastName: '',
      email: '',
      role: null,
      roleId: null,
      sendWelcomeEmail: true,
      note: null,
      saving: false,
      errors: {},
      groups: [],
      groupsLoading: false,
      selectedGroups: {},
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSaveUser = this.handleSaveUser.bind(this);
    this.handleChangeRole = this.handleChangeRole.bind(this);
    this.addSelectedGroup = this.addSelectedGroup.bind(this);
    this.removeSelectedGroup = this.removeSelectedGroup.bind(this);
  }

  componentDidMount() {
    this.loadGroups();

    const { user } = this.props;

    if (user) {
      this.setState({
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        role: user.role,
        roleId: user.role_id,
        sendWelcomeEmail: true,
      });
    }
  }

  loadGroups() {
    this.setState({ groupsLoading: true });

    APIgetGroups({ all: true, restricted: true }).then((response) => {
      const { data } = response;
      if (data) {
        this.setState({ groups: data });
      }
      this.setState({ groupsLoading: false });
    });
  }

  validateForm() {
    const { intl } = this.props;
    const {
      email,
      role,
      roleId,
    } = this.state;
    const errors = {};

    if (!email) {
      errors.email = intl.formatMessage({ defaultMessage: 'This field is required' });
    }

    if (!role && !roleId) {
      errors.role = intl.formatMessage({ defaultMessage: 'This field is required' });
    }

    this.setState({ errors });

    if (Object.keys(errors).length > 0) {
      return false;
    }

    return true;
  }

  handleChange(e) {
    const {
      name,
      value,
      checked,
    } = e.target;

    if (name === 'send_welcome_email') {
      this.setState({ sendWelcomeEmail: checked });
    } else {
      this.setState({ [name]: value });
    }
  }

  addSelectedGroup(groupUuid) {
    const { selectedGroups } = this.state;
    selectedGroups[groupUuid] = groupUuid;
    this.setState({ selectedGroups });
  }

  removeSelectedGroup(groupUuid) {
    const { selectedGroups } = this.state;
    this.setState({
      selectedGroups: Object.keys(selectedGroups)
        .filter(sg => (sg !== groupUuid))
        .reduce((cur, key) => (Object.assign(cur, { [key]: selectedGroups[key] })), {}),
    });
  }

  handleSaveUser() {
    if (!this.validateForm()) {
      return;
    }

    const {
      firstName,
      lastName,
      email,
      role,
      roleId,
      sendWelcomeEmail,
      note,
      selectedGroups,
    } = this.state;
    const {
      user,
      handleClose,
      notificationCallback,
    } = this.props;

    this.setState({ saving: true });

    let updatedUser = {};
    if (user) {
      updatedUser = Object.assign({}, user);
    }
    updatedUser.first_name = firstName;
    updatedUser.last_name = lastName;
    updatedUser.email = email;
    updatedUser.role = role;
    updatedUser.role_id = roleId;
    updatedUser.groups = Object.keys(selectedGroups).map(g => ({ uuid: g }));
    if (user) {
      APIupdateUser(updatedUser)
        .then((response) => {
          if (response.data) {
            notificationCallback(response.data);
            handleClose();
          }
        })
        .catch((error) => {
          this.setState({
            errors: parseResponseErrors(error.response),
            saving: false,
          });
        });
    } else {
      updatedUser.send_welcome_email = sendWelcomeEmail;
      updatedUser.note = note;
      APIcreateUser(updatedUser)
        .then((response) => {
          if (response.data) {
            notificationCallback(response.data);
            sendTrackingEvent('user-created', { uuid: response.data.uuid });
            performOnboardingStep('user-invited');
            handleClose();
          }
        })
        .catch((error) => {
          this.setState({
            errors: parseResponseErrors(error.response),
            saving: false,
          });
        });
    }
  }

  handleChangeRole(newRole) {
    const { value } = newRole;
    let {
      role,
      roleId,
    } = this.state;

    if (['admin', 'manager', 'user'].includes(value)) {
      roleId = null;
      role = value;
    } else {
      roleId = value;
      if (roleId) {
        role = 'user';
      }
    }

    this.setState({
      role,
      roleId,
    });
  }

  render() {
    const {
      firstName,
      lastName,
      email,
      role,
      roleId,
      errors,
      saving,
      selectedGroups,
      groups,
      groupsLoading,
      sendWelcomeEmail,
    } = this.state;
    const {
      intl,
      user,
      handleClose,
    } = this.props;

    return (
      <ErrorBoundary>
        <ModalHeader toggle={handleClose} className="modal-header-center">
          {user ? (
            intl.formatMessage({ defaultMessage: 'Edit user' })
          ) : (
            intl.formatMessage({ defaultMessage: 'Invite new user' })
          )}
        </ModalHeader>
        <ModalBody>
          <Form
            className="new-user-form"
          >
            {errors.error && (
              <p className="text-danger">
                {errors.error}
              </p>
            )}
            <AdminUsersLimitWarning />
            <Row form>
              <Col>
                <FormGroup>
                  <Label for="inpModalUserFirstName">
                    {intl.formatMessage({ defaultMessage: 'First name' })}
                  </Label>
                  <Input
                    type="text"
                    name="firstName"
                    id="inpModalUserFirstName"
                    placeholder=""
                    autoFocus={true}
                    defaultValue={firstName}
                    onChange={this.handleChange}
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Label for="inpModalUserLastName">
                    {intl.formatMessage({ defaultMessage: 'Last name' })}
                  </Label>
                  <Input
                    type="text"
                    name="lastName"
                    id="inpModalUserLastName"
                    placeholder=""
                    defaultValue={lastName}
                    onChange={this.handleChange}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row form>
              <Col>
                <FormGroup>
                  <Label for="inpModalUserEmail">
                    {intl.formatMessage({ defaultMessage: 'Email' })}
                  </Label>
                  <Input
                    type="text"
                    name="email"
                    id="inpModalUserEmail"
                    placeholder=""
                    defaultValue={email}
                    onChange={this.handleChange}
                    invalid={errors.email !== undefined}
                  />
                  {errors.email !== undefined && (
                    <FormFeedback>{errors.email}</FormFeedback>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row form>
              <Col>
                <FormGroup className={classnames({ 'is-invalid': errors.role !== undefined })}>
                  <Label for="selectUserRole">
                    {intl.formatMessage({ defaultMessage: 'What Role should this user have?' })}
                  </Label>
                  <AdminUserEditRoleSelect
                    profile={{ role, role_id: roleId }}
                    isSelfEdit={false}
                    invalid={errors.role !== undefined}
                    handleChangeRole={this.handleChangeRole}
                  />
                  {errors.role !== undefined && (
                    <FormFeedback>{errors.role}</FormFeedback>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row form>
              <Col>
                <FormGroup check>
                  <Label for="inpModalSendWelcomeEmail">
                    <Input
                      type="checkbox"
                      id="inpModalSendWelcomeEmail"
                      name="send_welcome_email"
                      className="input-check check-s form-check-input no-margin"
                      defaultChecked={true}
                      onChange={this.handleChange}
                    />
                    {' '}
                    {intl.formatMessage({ defaultMessage: 'Send the new user a welcome email to activate their account' })}
                  </Label>
                </FormGroup>
                {sendWelcomeEmail && (
                  <FormGroup>
                    <Label for="inpModalNote" className="user-note">
                      {intl.formatMessage({ defaultMessage: 'Add an optional note' })}
                      {!userHasFeature('user-invite-note') && (
                        <UpgradePlanButton
                          feature="user-invite-note"
                          simple={true}
                          forceUpgrade={true}
                        />
                      )}
                    </Label>
                    <Textarea
                      className="form-control"
                      disabled={!userHasFeature('user-invite-note')}
                      name="note"
                      id="inpModalNote"
                      onInput={this.handleChange}
                    />
                  </FormGroup>
                )}
              </Col>
            </Row>
            <Row form>
              <Col>
                <FormGroup>
                  <Label for="inpModalAccessAddGroup">
                    {intl.formatMessage({ defaultMessage: 'What Team(s) should this user be in?' })}
                  </Label>
                  <AccessModalSelect
                    groups={groups}
                    selectedGroups={selectedGroups}
                    users={[]}
                    loading={groupsLoading}
                    listType="groups"
                    handleAddItem={this.addSelectedGroup}
                  />
                </FormGroup>
                <AccessModalList
                  groups={groups}
                  selectedGroups={selectedGroups}
                  users={[]}
                  listType="groups"
                  mode="groups"
                  filesCount={0}
                  collectionsCount={0}
                  collectionGroupAllowedUuids={[]}
                  hasItemAccessToCollectionGroup={() => (true)}
                  handleAddItem={this.addSelectedGroup}
                  handleRemoveItem={this.removeSelectedGroup}
                />
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter>
          <div className="buttons-group">
            <Button
              color="secondary"
              disabled={saving}
              onClick={handleClose}
            >
              {intl.formatMessage({ defaultMessage: 'Cancel' })}
            </Button>
            <Button
              color="primary"
              disabled={saving}
              onClick={this.handleSaveUser}
            >
              <ProcessingSpinner show={saving} />
              {intl.formatMessage({ defaultMessage: 'Save' })}
            </Button>
          </div>
        </ModalFooter>
      </ErrorBoundary>
    );
  }
}

UserEditModal.propTypes = {
  intl: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  notificationCallback: PropTypes.func,
};

UserEditModal.defaultProps = {
  user: null,
  notificationCallback: () => { },
};

export default injectIntl(UserEditModal);
